import { Buffer } from 'buffer';
import iconv from 'iconv-lite';

import { PrinterContext } from './printer';

const parseASCIIOnlyData = (textBytes: string[]): string => {
  const textData: string[] = [];
  for (const byte of textBytes) {
    // Convert ascii unprintable char to space char
    if (/^[\x20-\x7e]*$/.test(byte)) textData.push(byte);
    else textData.push('\x20');
  }

  return textData.join('');
};

const parseFromEncoding = (textBytes: string[], encoding: string): string => {
  if (!iconv.encodingExists(encoding)) {
    console.log(`[Warning]: Encoding of ${encoding} is not exist in iconv-lite library`);
    return null;
  }

  const decodedString = iconv.decode(Buffer.from(textBytes.map((x) => x.charCodeAt(0))), encoding);

  /**
   * Side channel analysis to know if its a 1-byte encoding or 2-byte encoding
   * or ... n-byte encoding by subtracting the length of original textBytes
   * with the decoded one and find the modulo of the resulting value
   */
  const encodingByteLength = (() => {
    if (textBytes.length - decodedString.length === 0) return 1;

    // Ignore more than 4 byte length since its most likely something wrong happened
    for (const byteLength of [2, 3, 4]) {
      if ((textBytes.length - decodedString.length) % byteLength === 0) return byteLength;
    }
  })();

  const correctedString = [];
  let encodedStringIndex = -1;

  for (const string of decodedString.split('')) {
    encodedStringIndex += 1;

    if (string === textBytes[encodedStringIndex] && (!/^[\x20-\x7e]*$/.test(string) || string === '�')) {
      correctedString.push('\x20');
    } else {
      correctedString.push(string);

      // Skip next n bytes (of the textBytes) if its a decoded string
      if (string !== textBytes[encodedStringIndex]) {
        for (let i = 1; i < encodingByteLength; i++) {
          encodedStringIndex += 1;
        }
      }
    }
  }

  return correctedString.join('');
};

const encodeFrom = (textData: string, encoding: string): string[] => {
  if (!iconv.encodingExists(encoding)) {
    console.log(`[Warning]: Encoding of ${encoding} is not exist in iconv-lite library`);
    return null;
  }

  return iconv
    .encode(textData, encoding)
    .toString('hex')
    .match(/[a-fA-F0-9]{2}/g)
    .map((x: string) => String.fromCharCode(parseInt(x, 16)));
};

/**
 * TODO: Refactor this codepages mapping to use
 * https://github.com/receipt-print-hq/escpos-printer-db/blob/master/dist/capabilities.json
 * instead and then create a store specific config that specify what printer they are using
 *
 * This one is currently based on merged encoding between these two specifications
 * http://www.sam4s.co.kr/files/DOWN/2019012393432_1.pdf
 * https://www.novopos.ch/client/EPSON/TM-T20/TM-T20_eng_qr.pdf
 *
 * And also remember that we are only using iconv-lite library to decode
 * all this codepages so there are some unsupported codepages by iconv-lite
 * that exist here, check https://github.com/ashtuchkin/iconv-lite/wiki/Supported-Encodings for more
 */
export const escposCodepagesMapping = () => {
  return {
    0: 'cp437',
    1: 'cp932', // Katakana is basically the same as IBM codepage 932
    2: 'cp850',
    3: 'cp860',
    4: 'cp863',
    5: 'cp865',
    11: 'cp851',
    12: 'cp853',
    13: 'cp857',
    14: 'cp737',
    15: 'iso-8859-7',
    16: 'cp1252',
    17: 'cp866',
    18: 'cp852',
    19: 'cp858',
    20: 'tis620', // There are no KU42 Thai specific codepages so fallback to TIS620 Thai
    21: 'tis620', // There are no TIS11 Thai specific codepages so fallback to TIS620 Thai
    26: 'tis620', // There are no TIS11 Thai specific codepages so fallback to TIS620 Thai
    27: 'cp1098',
    30: 'tcvn',
    31: 'tcvn',
    32: 'cp720',
    33: 'cp775',
    34: 'cp855',
    35: 'cp861',
    36: 'cp862',
    37: 'cp864',
    38: 'cp869',
    39: 'iso-8859-2',
    40: 'iso-8859-15',
    41: 'cp1098',
    42: 'cp1118',
    43: 'cp1119',
    44: 'cp1125',
    45: 'cp1250',
    46: 'cp1251',
    47: 'cp1253',
    48: 'cp1254',
    49: 'cp1255',
    50: 'cp1256',
    51: 'cp1257',
    52: 'cp1258',
    53: 'rk1048',
    95: 'tis620',
    96: 'tis620', // There are no KU42 Thai specific codepages so fallback to TIS620 Thai
    97: 'tis620', // There are no TIS14 Thai specific codepages so fallback to TIS620 Thai
    98: 'tis620', // There are no TIS16 Thai specific codepages so fallback to TIS620 Thai
    99: 'cp1097',
  };
};

export const encodeTextData = (textData: string, encoding?: string) => {
  try {
    if (encoding !== '') {
      if (encoding === 'unicode') {
        return [...Buffer.from(textData)].map((x: number) => String.fromCharCode(x));
      } else return encodeFrom(textData, encoding);
    } else {
      return textData.split('');
    }
  } catch (err) {
    console.log(`[Warning]: Something went wrong while encoding text bytes (${err})`);
    return [];
  }
};

export const parseTextBytes = (textBytes: string[], encoding?: string) => {
  try {
    if (encoding !== '') {
      if (encoding === 'unicode') {
        return decodeURIComponent('%'.concat(textBytes.map((x: string) => x.charCodeAt(0).toString(16).padStart(2, '0')).join('%')));
      } else return parseFromEncoding(textBytes, encoding);
    } else {
      return parseASCIIOnlyData(textBytes);
    }
  } catch (err) {
    console.log(`[Warning]: Something went wrong while parsing text bytes (${err})`);
    return '';
  }
};

// Text renderer rendering primitives
export const text = (data: string, printerContext: PrinterContext, possibleMaximumWidth: number) => {
  switch (printerContext.getTextState().position) {
    case 'center':
      return data.padStart(Math.floor((possibleMaximumWidth - data.length) / 2) + data.length, ' ');

    case 'right':
      return data.padStart(possibleMaximumWidth, ' ');

    case 'left':
    default:
      return data;
  }
};
